/**
 * Projeção do mapa de visitas (spec mapa-contorno-brasil, design §3; ADR-032).
 *
 * Equiretangular com correção de escala pela latitude de referência, em "unidades do mundo" (células de
 * `grade` graus). O contorno das UFs (`assets/mapa/ufs-brasil.json`, gerado por
 * `tools/bases-referencia/build_ufs_mapa.py`) usa exatamente estes parâmetros — um teste de contrato confere.
 * O SVG desenha tudo nessas unidades; trocar o enquadramento é só trocar o `viewBox`.
 */

export interface ParametrosProjecao {
  readonly lng0: number;
  readonly lat0: number;
  readonly latRef: number;
  readonly grade: number;
}

/** Fonte única da projeção (o asset grava os mesmos valores). */
export const PROJECAO: ParametrosProjecao = { lng0: -74, lat0: 6, latRef: -14.24, grade: 0.005 };

/** Caixa em unidades do mundo: [x0, y0, x1, y1]. */
export type Caixa = readonly [number, number, number, number];

/** Uma UF do contorno: `d` é um `path` SVG em unidades do mundo (M absoluto, l relativo, z). */
export interface UfContorno {
  readonly sigla: string;
  readonly nome: string;
  readonly bbox: Caixa;
  readonly d: string;
}

/** Conteúdo de `assets/mapa/ufs-brasil.json` (design §4). */
export interface ContornoUfs {
  readonly versao: string;
  readonly fonte: string;
  readonly projecao: ParametrosProjecao;
  readonly simplificacao: { readonly tolerancia: number; readonly hausdorffMaxKm: number };
  readonly brasil: { readonly bbox: Caixa };
  readonly ufs: readonly UfContorno[];
}

/** Caixa do Brasil (igual a `brasil.bbox` do asset — conferida por teste). Usada sem o contorno. */
export const BBOX_BRASIL_PADRAO: Caixa = [3, 146, 8068, 7950];

/** Proporção fixa do quadro na tela (próxima da do Brasil, para a altura não mudar ao enquadrar uma UF). */
export const LARGURA_QUADRO = 640;
export const ALTURA_QUADRO = 620;

const MARGEM_BRASIL = 0.03;
const MARGEM_UF = 0.08;
/** Lado mínimo do quadro de uma UF, em graus (o DF não vira zoom excessivo). */
const LADO_MINIMO_UF_GRAUS = 0.6;

export interface Ponto2D {
  readonly x: number;
  readonly y: number;
}

export interface Quadro {
  readonly tipo: 'brasil' | 'uf';
  readonly x: number;
  readonly y: number;
  readonly largura: number;
  readonly altura: number;
  /** No quadro do Brasil: a caixa onde as coordenadas são presas (PC-02). */
  readonly limite: Caixa;
}

/** (lng, lat) em graus → unidades do mundo. Função pura. */
export function projetarMundo(lng: number, lat: number, p: ParametrosProjecao = PROJECAO): Ponto2D {
  const escala = Math.cos((p.latRef * Math.PI) / 180);
  return { x: ((lng - p.lng0) * escala) / p.grade, y: (p.lat0 - lat) / p.grade };
}

/** Expande uma caixa (centrada) até a proporção LARGURA_QUADRO:ALTURA_QUADRO. */
function ajustarProporcao(x0: number, y0: number, x1: number, y1: number): [number, number, number, number] {
  const alvo = LARGURA_QUADRO / ALTURA_QUADRO;
  let largura = x1 - x0;
  let altura = y1 - y0;
  if (largura / altura > alvo) {
    altura = largura / alvo;
  } else {
    largura = altura * alvo;
  }
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  return [cx - largura / 2, cy - altura / 2, largura, altura];
}

/** Quadro do Brasil inteiro: a caixa do Brasil com 3% de margem, na proporção do quadro. */
export function quadroBrasil(bbox: Caixa = BBOX_BRASIL_PADRAO): Quadro {
  const [x0, y0, x1, y1] = bbox;
  const mx = (x1 - x0) * MARGEM_BRASIL;
  const my = (y1 - y0) * MARGEM_BRASIL;
  const [x, y, largura, altura] = ajustarProporcao(x0 - mx, y0 - my, x1 + mx, y1 + my);
  return { tipo: 'brasil', x, y, largura, altura, limite: bbox };
}

/** Quadro de uma UF: a caixa da UF com 8% de margem, lado mínimo de 0,6°, na proporção do quadro. */
export function quadroUf(bboxUf: Caixa, p: ParametrosProjecao = PROJECAO): Quadro {
  const [x0, y0, x1, y1] = bboxUf;
  const minimo = LADO_MINIMO_UF_GRAUS / p.grade;
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const meiaL = Math.max((x1 - x0) * (1 + 2 * MARGEM_UF), minimo) / 2;
  const meiaA = Math.max((y1 - y0) * (1 + 2 * MARGEM_UF), minimo) / 2;
  const [x, y, largura, altura] = ajustarProporcao(cx - meiaL, cy - meiaA, cx + meiaL, cy + meiaA);
  return { tipo: 'uf', x, y, largura, altura, limite: bboxUf };
}

/** Valor do atributo `viewBox` do SVG para o quadro. */
export function viewBox(q: Quadro): string {
  return [q.x, q.y, q.largura, q.altura].map((v) => Number(v.toFixed(2))).join(' ');
}

/**
 * Posição de um ponto (lng, lat) no quadro, em unidades do mundo.
 * - Quadro do Brasil: a coordenada é **presa** na caixa do Brasil — todo ponto desenhado fica dentro do quadro
 *   (invariante PC-02 do M10).
 * - Quadro de UF: coordenada fora do quadro → `null` (o ponto não é desenhado; Req 4 AC2).
 * - Coordenada não finita → `null`.
 */
export function projetarNoQuadro(lng: number, lat: number, q: Quadro, p: ParametrosProjecao = PROJECAO): Ponto2D | null {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }
  const { x, y } = projetarMundo(lng, lat, p);
  if (q.tipo === 'brasil') {
    const [x0, y0, x1, y1] = q.limite;
    return { x: Math.min(Math.max(x, x0), x1), y: Math.min(Math.max(y, y0), y1) };
  }
  const dentro = x >= q.x && x <= q.x + q.largura && y >= q.y && y <= q.y + q.altura;
  return dentro ? { x, y } : null;
}

/** Raio, em unidades do mundo, que aparece com `px` pixels num quadro desenhado com LARGURA_QUADRO px. */
export function raioNoQuadro(q: Quadro, px = 5): number {
  return (px * q.largura) / LARGURA_QUADRO;
}
